<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from "vue";

// pieceCount is tuned for a full-screen layer; a small host (the review card) passes fewer.
const props = withDefaults(defineProps<{ durationMs?: number; pieceCount?: number }>(), {
  durationMs: 3500,
  pieceCount: 280,
});
const canvas = ref<HTMLCanvasElement | null>(null);
let frameId: number | null = null;

type Piece = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  rotation: number;
  spin: number;
  drift: number;
  color: string;
};

const GRAVITY = 0.07;
const FADE_START = 0.7;
// Vertical travel from launch to apex, as a fraction of the canvas height — targets the top quarter.
const APEX_FRACTION = 0.75;
const COLOR_TOKENS = [
  "--color-app-blue-500",
  "--color-app-blue-300",
  "--color-app-green-500",
  "--color-app-yellow-500",
  "--color-app-purple-500",
];

const readColors = (): string[] => {
  const styles = getComputedStyle(document.documentElement);
  return COLOR_TOKENS.map((token) => styles.getPropertyValue(token).trim()).filter((color) => color.length > 0);
};

// Two bursts near the bottom corners, shooting up and inward. vy is derived from the canvas height
// (via GRAVITY and APEX_FRACTION) so the arcs reach roughly the same point regardless of layer size.
const spawnPieces = (width: number, height: number, colors: string[], count: number): Piece[] => {
  const launchSpeed = Math.sqrt(2 * GRAVITY * height * APEX_FRACTION);
  const origins = [
    { x: width * 0.1, tilt: 1 },
    { x: width * 0.9, tilt: -1 },
  ];
  return Array.from({ length: count }, (_, index) => {
    const origin = origins[index % origins.length];
    return {
      x: origin.x,
      y: height,
      vx: origin.tilt * launchSpeed * (0.3 + Math.random() * 0.35),
      vy: -launchSpeed * (0.85 + Math.random() * 0.3),
      size: 8 + Math.random() * 6,
      rotation: Math.random() * 360,
      spin: (Math.random() - 0.5) * 12,
      drift: (Math.random() - 0.5) * 0.05,
      color: colors[Math.floor(Math.random() * colors.length)],
    };
  });
};

onMounted(() => {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  const element = canvas.value;
  const context = element?.getContext("2d");
  if (!element || !context) return;
  const ratio = window.devicePixelRatio || 1;
  const width = element.clientWidth;
  const height = element.clientHeight;
  element.width = width * ratio;
  element.height = height * ratio;
  context.scale(ratio, ratio);
  const colors = readColors();
  if (colors.length === 0) return;
  const pieces = spawnPieces(width, height, colors, props.pieceCount);
  const startedAt = performance.now();
  const fadeStartAt = props.durationMs * FADE_START;

  const draw = (now: number) => {
    const elapsed = now - startedAt;
    context.clearRect(0, 0, width, height);
    if (elapsed >= props.durationMs) {
      frameId = null;
      return;
    }
    const alpha = elapsed <= fadeStartAt ? 1 : 1 - (elapsed - fadeStartAt) / (props.durationMs - fadeStartAt);
    for (const piece of pieces) {
      piece.vy += GRAVITY;
      piece.vx += piece.drift;
      piece.x += piece.vx;
      piece.y += piece.vy;
      piece.rotation += piece.spin;
      context.save();
      context.globalAlpha = alpha;
      context.translate(piece.x, piece.y);
      context.rotate((piece.rotation * Math.PI) / 180);
      context.fillStyle = piece.color;
      context.fillRect(-piece.size / 2, -piece.size / 2, piece.size, piece.size * 0.6);
      context.restore();
    }
    frameId = requestAnimationFrame(draw);
  };
  frameId = requestAnimationFrame(draw);
});

onBeforeUnmount(() => {
  if (frameId !== null) cancelAnimationFrame(frameId);
});
</script>

<template>
  <canvas
    ref="canvas"
    aria-hidden="true"
    class="pointer-events-none absolute inset-0 h-full w-full" />
</template>
